import { AimOutlined, EnvironmentFilled, EnvironmentOutlined } from '@ant-design/icons';
import { AutoComplete, Input, Skeleton } from 'antd';
import { reverseGeoCode } from 'api/geocode';
import useAutoComplete from 'hooks/useAutoComplete';
import React, { useEffect, useState } from 'react';
import {
  GoogleMap, useJsApiLoader, Marker,
} from '@react-google-maps/api';
import { captureException } from 'utils/errors';
import { getGeoCode } from 'utils/places';

export const GOOGLE_MAP_LIBRARIES = ['places'];

function normalizeLatLng(latLng) {
  if (!latLng) return {};
  return {
    lat: typeof latLng.lat === 'function' ? latLng.lat() : latLng.lat,
    lng: typeof latLng.lng === 'function' ? latLng.lng() : latLng.lng,
  };
}

function BaseMap({
  children,
  onChange = () => {
  },
  onMapReady,
  onMapClick,
  mapContainerId,
  preventMapClick = false,
  center: defaultCenter,
  height = 300,
  style,
  zoom = 15,
  ...mapOptions
}) {
  const [map, setMap] = useState(null);
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: 'AIzaSyBiGU97-Nd44zat6xJvd93Na6l2m61Y-Zw',
    libraries: GOOGLE_MAP_LIBRARIES,
  });

  const debouncedReverseGeocode = React.useCallback(
    React.useMemo(() => {
      let timeoutId;
      return (newCenter) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          reverseGeoCode(normalizeLatLng(newCenter))
            .then((addressData) => {
              // addressData now includes: text, city, state, country, country_code, zip_code
              onChange({
                ...normalizeLatLng(newCenter),
                text: addressData.text,
                city: addressData.city,
                state: addressData.state,
                country: addressData.country,
                country_code: addressData.country_code,
                zip_code: addressData.zip_code,
              });
            })
            .catch((e) => {
              captureException(e);
              onChange(normalizeLatLng(newCenter));
            });
        }, 1000);
      };
    }, [onChange]),
    [onChange],
  );

  const onCenterChange = (newCenter) => {
    debouncedReverseGeocode(newCenter);
  };

  const onLoad = React.useCallback((loadedMap) => {
    setMap(loadedMap);
    onMapReady?.(loadedMap);
  }, [onMapReady]);

  const onUnmount = React.useCallback(() => {
    setMap(null);
  }, []);

  return isLoaded ? (
    <GoogleMap
      id={mapContainerId}
      mapContainerStyle={{
        height,
        width: '100%',
        ...style,
      }}
      center={defaultCenter}
      zoom={zoom}
      onLoad={onLoad}
      onUnmount={onUnmount}
      onClick={(e) => {
        onMapClick?.(e.latLng);
        if (!preventMapClick) {
          onCenterChange(e.latLng);
        }
      }}
      onCenterChanged={preventMapClick ? undefined : () => {
        if (!map) {
          return;
        }
        onCenterChange(map.getCenter());
      }}
      options={{
        streetViewControl: false,
        mapTypeControl: false,
        gestureHandling: 'greedy',
        styles: [
          {
            featureType: 'poi',
            stylers: [
              { visibility: 'off' },
            ],
          },
        ],
        ...mapOptions,
      }}
    >
      {children}
    </GoogleMap>
  ) : <MapSkeleton height={height} />;
}

const Map = React.memo(BaseMap);

Map.FixedMarker = function (props) {
  return (
    <EnvironmentFilled
      {...props}
      style={{
        position: 'absolute',
        left: 'calc( 50% - 15px)',
        top: 'calc( 50% - 15px)',
        fontSize: 32,
        cursor: 'grab',
        ...props.style,
      }}
    />
  );
};

Map.Marker = Marker;

Map.SearchInput = function SearchInput({
  onChange, value: defaultAddress, hasCurrentLocationSelector = true, ...props
}) {
  const [value, setValue] = useState('');
  const options = useAutoComplete(value);
  async function handleSelect(_, selectedOption) {
    if (!selectedOption || typeof selectedOption !== 'object') {
      return;
    }
    const {
      place_id: placeId,
      description: text,
    } = selectedOption;
    const geoData = await getGeoCode(placeId);
    // Pass all address data including city, state, zip_code, country, country_code
    onChange({
      lat: geoData.lat,
      lng: geoData.lng,
      text,
      city: geoData.city,
      state: geoData.state,
      country: geoData.country,
      country_code: geoData.country_code,
      zip_code: geoData.zip_code,
    });
  }

  useEffect(() => {
    if (defaultAddress?.place_id) handleSelect('', defaultAddress);
  }, [defaultAddress]);

  const handleCurrentLocation = async () => {
    if (navigator?.geolocation) {
      await navigator.geolocation.getCurrentPosition(success, fail);
    } else {
      alert('You should set location permission');
    }

    function success(position) {
      const { coords: { latitude: lat, longitude: lng } } = position;
      onChange({ lat, lng });
    }

    function fail() {
      alert('You should set location permission!');
    }
  };

  return (
    <AutoComplete
      options={options}
      style={{ width: '85%', top: 10, left: 8 }}
      onSelect={handleSelect}
      onSearch={setValue}
    >
      <Input
        prefix={<EnvironmentOutlined />}
        addonAfter={hasCurrentLocationSelector ? <AimOutlined onClick={handleCurrentLocation} /> : null}
        placeholder="Enter the delivery address"
        size="large"
      />
    </AutoComplete>
  );
};

function MapSkeleton({ height }) {
  return (
    <Skeleton.Input block fullSize active style={{ height }} />
  );
}

export default Map;
